import {
  ButtonGroup,
  Button as MuiButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModalStore } from "../../store/useModalStore";
import type { UpdateDeviceForm, DEVICE_CATEGORY_TYPE, getDeviceListForm } from "../../lib/apis/dtos/request/device.request";
import { deviceErrorMessage, invalidateDeviceQueries } from "../../lib/apis/device-cache";
import FailSnackbar from "../../components/ui/snackbar/fail.component";
import { useAdminDeviceList } from "../../lib/apis/hooks/device.hook";
import { patchDevice } from "../../lib/apis/v1/device.service";
import { getQueryClient } from "../../config/queryClient.config";
import { useEnterKey } from "../../lib/hooks/useEnterKey";
import SearchInput from "../../components/ui/input/search.component";
import Button from "../../components/ui/button";
import { MODAL_KEY } from "../../lib/constants/modal.constants";

const CATEGORY_MENU = {
  IPHONE: "아이폰",
  GALAXY: "갤럭시",
  ETC: "기타",
};

const PHONE_GROUP_MENU = [
  { label: "전체", value: "ALL" },
  { label: "주요폰", value: "MAIN" },
  { label: "중저가폰", value: "LOW" },
  { label: "키즈폰", value: "KIDS" },
];

const DEFAULT_MENU = [
  { label: "전체", value: false },
  { label: "시세표 기본 포함", value: true },
];

const DEFAULT_PAGE_SIZE = 10;

export default function ModelPage() {
  // ROUTER
  const navigate = useNavigate();

  // STATE
  const [form, setForm] = useState<getDeviceListForm>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    search: "",
    category: "ALL",
    defaultOnly: false,
  });

  const [searchInput, setSearchInput] = useState("");
  const { openModal } = useModalStore();

  const { data, refetch, isSuccess, isError, error, isPending } = useQuery({
    ...useAdminDeviceList(form),
    enabled: true,
  });
  const { result } = data || {};
  const content = result?.content || [];
  const page = result?.page;

  const patchMutation = useMutation({
    mutationFn: ({ id, form }: { id: string; form: UpdateDeviceForm }) =>
      patchDevice(id, form),
    onSuccess: () => invalidateDeviceQueries(getQueryClient()),
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  // HANDLER
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setForm((state) => ({ ...state, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((state) => ({
      ...state,
      page: 0,
      size: parseInt(event.target.value, 10),
    }));
  };

  const applySearch = () => {
    const search = searchInput.trim();
    if (form.search === search && form.page === 0) void refetch();
    else setForm((state) => ({ ...state, page: 0, search }));
  };
  useEnterKey(applySearch);

  return (
    <div className="p-6">
      <div className="flex items-center p-[16px]">
        <SearchInput
          onChange={(e) =>
            setSearchInput(e.target.value)
          }
          label="기종 검색"
          placeholder="기종코드, 기종명으로 검색"
          onClick={applySearch}
        />
        <div className="h-[32px] w-[0.5px] border-[0.5px] mx-[24px] " />
        <Button
          label="기종 생성"
          className="bg-[#ffffff] text-[14px] w-[120px] h-[36px] text-[#1976D2]  rounded-[3px] border border-[#1976D280]"
          onClick={() => openModal(MODAL_KEY.DeviceCreate, {})}
        />
      </div>
      <div className="flex flex-row justify-between items-center px-[16px] pt-[8px] mb-[8px]">
        <p className="text-[14px] font-medium">
          전체 {page?.totalElements || 0}개
        </p>

        <div className="float-right space-x-[16px]">
          <ButtonGroup aria-label="account-group">
            {PHONE_GROUP_MENU.map(({ label, value }, index) => (
              <MuiButton
                key={index}
                style={{
                  backgroundColor:
                    form.category === value
                      ? "rgba(25, 118, 210, 0.5)"
                      : undefined,
                }}
                variant={form.category == value ? "contained" : "outlined"}
                onClick={() =>
                  setForm((state) => ({
                    ...state,
                    category: value as DEVICE_CATEGORY_TYPE,
                    page: 0,
                  }))
                }
              >
                {label}
              </MuiButton>
            ))}
          </ButtonGroup>

          <ButtonGroup aria-label="model-group">
            {DEFAULT_MENU.map(({ label, value }, index) => (
              <MuiButton
                key={index}
                style={{
                  backgroundColor:
                    form.defaultOnly === value
                      ? "rgba(25, 118, 210, 0.5)"
                      : undefined,
                }}
                variant={form.defaultOnly == value ? "contained" : "outlined"}
                onClick={() =>
                  setForm((state) => ({
                    ...state,
                    defaultOnly: value,
                    page: 0,
                  }))
                }
              >
                {label}
              </MuiButton>
            ))}
          </ButtonGroup>
        </div>
      </div>

      {isError && <p role="alert">{deviceErrorMessage(error)} <button onClick={() => void refetch()}>다시 시도</button></p>}
      {isPending && <p role="status">기종 목록을 불러오는 중입니다.</p>}
      {isSuccess && content.length === 0 && <p>검색 결과가 없습니다.</p>}
      <TableContainer>
        <Table className="w-full">
          <TableHead>
            <TableRow>
              <TableCell>모델 고유 번호</TableCell>
              <TableCell>카테고리 구분</TableCell>
              <TableCell>모델 구분</TableCell>
              <TableCell>기종명</TableCell>
              <TableCell>용량(GB)</TableCell>
              <TableCell>시세표 기본 포함</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isSuccess &&
              (content || []).map((items, index) => {
                const {
                  deviceId,
                  deviceName,
                  isDefault,
                  sizeAvailable,
                  category,
                  deviceCode,
                  mainCheck,
                  lowCheck,
                  kidsCheck,
                } = items;
                const _storage = [...(sizeAvailable ?? [])].sort().join(", ");
                return (
                  <TableRow
                    onClick={() =>
                      navigate(`/model/${deviceId}`)
                    }
                    key={`model_${index}`}
                  >
                    <TableCell>{deviceCode}</TableCell>
                    <TableCell>
                      {[
                        mainCheck && '주요폰',
                        lowCheck && '중저가폰',
                        kidsCheck && '키즈폰',
                      ].filter(Boolean).join(', ')}
                    </TableCell>
                    <TableCell>
                      {category
                        ? CATEGORY_MENU[category as keyof typeof CATEGORY_MENU]
                        : ""}
                    </TableCell>
                    <TableCell>{deviceName}</TableCell>
                    <TableCell>{_storage || "-"}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={!!isDefault}
                        disabled={patchMutation.isPending}
                        onClick={() => {
                          patchMutation.mutate({
                            id: deviceId.toString(),
                            form: {
                              isDefault: !isDefault,
                            },
                          });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={page?.totalElements || 0}
        rowsPerPage={form.size}
        page={form.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}
